import type {
  MarketDomainServerMessage,
  MarketStreamSubscription,
} from '@hypersense/contracts/market';
import { isMarketDomainServerMessage } from '@hypersense/contracts/market';
import { createStore, type Store } from '@tanstack/react-store';

import { marketFeedRealtimeWebsocketUrl } from '@web/api/marketFeedRealtimeClient';
import { webObservability } from '@web/lib/observability';

import {
  applyMarketFeedSubscriptionChange,
  areMarketFeedSubscriptionsEqual,
  createInitialMarketFeedStreamState,
  type MarketFeedStreamStateT,
  reduceMarketFeedStreamMessage,
} from './marketFeedStreamState';
import { queueMarketFeedPendingMessage } from './queueMarketFeedPendingMessage';

const CLIENT_MAX_RECONNECT_DELAY_MS = 8_000;
const CLIENT_RECONNECT_BASE_DELAY_MS = 500;
const HIDDEN_FLUSH_DELAY_MS = 250;
const SESSION_DISPOSE_DELAY_MS = 1_000;

export interface MarketFeedStreamSessionT {
  mount(): void;
  preconnect(): void;
  store: Store<MarketFeedStreamStateT>;
  unmount(): void;
  updateSubscription(subscription: MarketStreamSubscription): void;
}

const marketFeedStreamSessions = new Map<string, MarketFeedStreamSessionT>();

export function getMarketFeedStreamSession(input: {
  asset: string;
  maxTradeCount: number;
}) {
  const existingSession = marketFeedStreamSessions.get(input.asset);

  if (existingSession !== undefined) {
    return existingSession;
  }

  const session = createMarketFeedStreamSession(input);
  marketFeedStreamSessions.set(input.asset, session);

  return session;
}

function createMarketFeedStreamSession(input: {
  asset: string;
  maxTradeCount: number;
}): MarketFeedStreamSessionT {
  const sessionKey = input.asset;
  const store = createStore(createInitialMarketFeedStreamState());
  let currentSubscription: MarketStreamSubscription | null = null;
  let disposeTimerId: ReturnType<typeof setTimeout> | null = null;
  let flushFrameId: number | null = null;
  let hiddenFlushTimerId: ReturnType<typeof setTimeout> | null = null;
  let mountedClientCount = 0;
  let pendingMessages: MarketDomainServerMessage[] = [];
  let reconnectAttemptCount = 0;
  let reconnectTimerId: ReturnType<typeof setTimeout> | null = null;
  let socket: WebSocket | null = null;

  function setState(
    updater: (currentState: MarketFeedStreamStateT) => MarketFeedStreamStateT,
  ) {
    store.setState((currentState) => {
      return updater(currentState);
    });
  }

  function clearDisposeTimer() {
    if (disposeTimerId === null) {
      return;
    }

    clearTimeout(disposeTimerId);
    disposeTimerId = null;
  }

  function clearReconnectTimer() {
    if (reconnectTimerId === null) {
      return;
    }

    clearTimeout(reconnectTimerId);
    reconnectTimerId = null;
  }

  function clearHiddenFlushTimer() {
    if (hiddenFlushTimerId === null) {
      return;
    }

    clearTimeout(hiddenFlushTimerId);
    hiddenFlushTimerId = null;
  }

  function clearScheduledFlush() {
    if (flushFrameId === null) {
      clearHiddenFlushTimer();
      return;
    }

    window.cancelAnimationFrame(flushFrameId);
    flushFrameId = null;
    clearHiddenFlushTimer();
  }

  function flushPendingMessages() {
    if (pendingMessages.length === 0) {
      return;
    }

    const nextMessages = pendingMessages;
    pendingMessages = [];

    setState((currentState) => {
      return nextMessages.reduce((reducedState, message) => {
        return reduceMarketFeedStreamMessage({
          currentState: reducedState,
          maxTradeCount: input.maxTradeCount,
          message,
        });
      }, currentState);
    });
  }

  function scheduleFlush() {
    if (pendingMessages.length === 0) {
      return;
    }

    if (readUsesTimeoutFlush(currentSubscription)) {
      if (hiddenFlushTimerId !== null) {
        return;
      }

      hiddenFlushTimerId = setTimeout(() => {
        hiddenFlushTimerId = null;
        flushPendingMessages();
      }, HIDDEN_FLUSH_DELAY_MS);
      return;
    }

    if (flushFrameId !== null) {
      return;
    }

    flushFrameId = window.requestAnimationFrame(() => {
      flushFrameId = null;
      flushPendingMessages();
    });
  }

  function reconcilePendingMessagesForSubscription() {
    if (
      !readUsesTimeoutFlush(currentSubscription) ||
      pendingMessages.length === 0
    ) {
      return;
    }

    let nextPendingMessages: MarketDomainServerMessage[] = [];

    for (const pendingMessage of pendingMessages) {
      nextPendingMessages = queueMarketFeedPendingMessage({
        currentPendingMessages: nextPendingMessages,
        nextMessage: pendingMessage,
        subscription: currentSubscription,
      });
    }

    pendingMessages = nextPendingMessages;
  }

  function connect() {
    if (socket !== null) {
      return;
    }

    setState((currentState) => {
      if (
        currentState.status === 'connecting' &&
        currentState.errorMessage === null
      ) {
        return currentState;
      }

      return {
        ...currentState,
        errorMessage: null,
        status: 'connecting',
      };
    });

    const nextSocket = new WebSocket(marketFeedRealtimeWebsocketUrl);
    socket = nextSocket;

    nextSocket.onopen = () => {
      if (socket !== nextSocket) {
        return;
      }

      clearReconnectTimer();
      reconnectAttemptCount = 0;

      if (currentSubscription !== null) {
        nextSocket.send(JSON.stringify(currentSubscription));
      }
    };

    nextSocket.onmessage = (event) => {
      if (socket !== nextSocket) {
        return;
      }

      const payload = parseMarketFeedStreamMessage(event.data);

      if (payload === null) {
        webObservability.logger.warn(
          'market-feed websocket emitted an invalid message payload',
          {
            operation: 'market_feed.ws',
          },
        );
        return;
      }

      pendingMessages = queueMarketFeedPendingMessage({
        currentPendingMessages: pendingMessages,
        nextMessage: payload,
        subscription: currentSubscription,
      });
      scheduleFlush();
    };

    nextSocket.onerror = (event) => {
      webObservability.captureException(event, {
        tags: {
          operation: 'market_feed.ws',
        },
      });
    };

    nextSocket.onclose = () => {
      if (socket === nextSocket) {
        socket = null;
      }

      if (mountedClientCount === 0) {
        return;
      }

      clearScheduledFlush();
      flushPendingMessages();
      setState((currentState) => {
        if (currentState.status === 'offline') {
          return currentState;
        }

        return {
          ...currentState,
          status: 'offline',
        };
      });
      scheduleReconnect();
    };
  }

  function scheduleReconnect() {
    if (mountedClientCount === 0 || reconnectTimerId !== null) {
      return;
    }

    reconnectAttemptCount += 1;
    const reconnectDelayMs = readReconnectDelayMs({
      attempt: reconnectAttemptCount,
      baseDelayMs: CLIENT_RECONNECT_BASE_DELAY_MS,
      maxDelayMs: CLIENT_MAX_RECONNECT_DELAY_MS,
    });

    reconnectTimerId = setTimeout(() => {
      reconnectTimerId = null;

      if (mountedClientCount > 0) {
        connect();
      }
    }, reconnectDelayMs);
  }

  function dispose() {
    clearDisposeTimer();
    clearReconnectTimer();
    clearScheduledFlush();
    pendingMessages = [];

    if (socket !== null) {
      socket.close();
      socket = null;
    }

    marketFeedStreamSessions.delete(sessionKey);
  }

  function scheduleDispose() {
    if (disposeTimerId !== null) {
      return;
    }

    disposeTimerId = setTimeout(() => {
      disposeTimerId = null;

      if (mountedClientCount === 0) {
        dispose();
      }
    }, SESSION_DISPOSE_DELAY_MS);
  }

  return {
    mount() {
      mountedClientCount += 1;
      clearDisposeTimer();
      connect();
    },
    preconnect() {
      connect();
    },
    store,
    unmount() {
      mountedClientCount = Math.max(0, mountedClientCount - 1);

      if (mountedClientCount === 0) {
        scheduleDispose();
      }
    },
    updateSubscription(nextSubscription) {
      if (
        currentSubscription !== null &&
        areMarketFeedSubscriptionsEqual(currentSubscription, nextSubscription)
      ) {
        return;
      }

      const previousSubscription = currentSubscription;
      currentSubscription = nextSubscription;

      store.setState((currentState) => {
        return applyMarketFeedSubscriptionChange({
          currentState,
          nextSubscription,
          previousSubscription,
        });
      });
      clearScheduledFlush();
      reconcilePendingMessagesForSubscription();
      scheduleFlush();

      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(currentSubscription));
      }
    },
  };
}

function parseMarketFeedStreamMessage(
  value: unknown,
): MarketDomainServerMessage | null {
  if (typeof value !== 'string') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);

    return isMarketDomainServerMessage(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function readReconnectDelayMs(input: {
  attempt: number;
  baseDelayMs: number;
  maxDelayMs: number;
}) {
  const exponentialDelayMs =
    input.baseDelayMs * 2 ** Math.max(0, input.attempt - 1);
  const cappedDelayMs = Math.min(input.maxDelayMs, exponentialDelayMs);
  const jitterMultiplier = 0.8 + Math.random() * 0.4;

  return Math.round(cappedDelayMs * jitterMultiplier);
}

function readUsesTimeoutFlush(subscription: MarketStreamSubscription | null) {
  if (subscription === null) {
    return false;
  }

  return (
    subscription.resources.bars === undefined &&
    subscription.resources.ladder === undefined &&
    subscription.resources.stats === true &&
    subscription.resources.trades === undefined
  );
}
